import { ArrowRight, Clock, Globe2, RefreshCw, ShieldCheck } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { api, type DossierStatus, type VnnicCoverage, type VnnicGap, type VnnicRegistryList, type VnnicRegistryMismatchKind } from '../../../api';
import { Badge, type Tone } from '../../../components/Badge';
import { Callout, DataTable, Section, StatCard, StatGrid, type Column } from '../../../components/blocks';
import { Meter } from '../../../components/Meter';
import { PageHeader, useDocumentTitle } from '../../../components/PageHeader';
import { EmptyState, ErrorState, Loading } from '../../../components/States';
import { useWrite, WriteError } from '../../../components/Write';
import { Button } from '../../../components/ui/button';
import { formatCount, formatDateTime, formatDuration, formatPercent, formatRelative } from '../../../lib/format';
import { cn } from '../../../lib/utils';
import { useApi } from '../../../lib/useApi';
import { DOSSIER } from '../../domains/status';
import { EnumBadge, useEnumLabel, type EnumGroup } from '../ui';
import { ageSeconds, GAP, GAP_ORDER, gapResolution, periodLabel, REPORT_STATUS, SYNC_STATUS, SYNC_STATUS_ORDER, VNNIC_BASE } from './shared';

const EXCEPTION_LIMIT = 200;

type Exception = Awaited<ReturnType<typeof api.listVnnicExceptions>>['items'][number];
type ReportRow = VnnicCoverage['reports'][number];

function ratioTone(ratio: number | null): Tone {
  if (ratio === null) return 'neutral';
  if (ratio >= 1) return 'ok';
  return ratio >= 0.95 ? 'warn' : 'serious';
}

function share(part: number, whole: number): number | null {
  return whole > 0 ? part / whole : null;
}

function isGap(value: string | null): value is VnnicGap {
  return value !== null && value in GAP;
}

function Summary({ data }: { data: VnnicCoverage }) {
  const { t } = useTranslation('compliance');
  const covered = share(data.fully_covered, data.managed_domains);
  const openGaps = GAP_ORDER.reduce((n, g) => n + data.gaps[g], 0);
  const oldestSeconds = ageSeconds(data.oldest_unacknowledged_sync_at);
  const tone = ratioTone(covered);
  return (
    <StatGrid className="xl:grid-cols-3">
      <StatCard
        label={t('vnnic.coverage.fullyCovered')}
        tone={tone === 'serious' ? 'crit' : tone}
        icon={ShieldCheck}
        value={
          <span className="flex items-baseline gap-2">
            {formatCount(data.fully_covered)}
            <span className="text-sm font-normal text-muted-foreground">
              {t('vnnic.coverage.ofTotal', {
                total: formatCount(data.managed_domains),
              })}
            </span>
          </span>
        }
        hint={
          <>
            {t('vnnic.coverage.coveredHint')} {openGaps ? t('vnnic.coverage.openExceptions', { count: openGaps }) : null}
          </>
        }
      >
        <div className="flex items-center gap-2">
          <Meter value={covered} tone={tone} label={t('vnnic.coverage.coveredMeter')} />
          <span className="text-xs font-medium tabular">{formatPercent(covered)}</span>
        </div>
      </StatCard>
      <StatCard
        label={t('vnnic.coverage.oldestSync')}
        tone={oldestSeconds === null ? 'ok' : oldestSeconds > 3 * 86_400 ? 'warn' : 'neutral'}
        icon={Clock}
        value={oldestSeconds === null ? t('common.none') : formatDuration(oldestSeconds)}
        hint={
          data.oldest_unacknowledged_sync_at
            ? t('vnnic.coverage.waitingSince', {
                at: formatDateTime(data.oldest_unacknowledged_sync_at),
                rel: formatRelative(data.oldest_unacknowledged_sync_at),
              })
            : t('vnnic.coverage.allAcknowledged')
        }
        to={`${VNNIC_BASE}/dossier-sync`}
        linkLabel={t('vnnic.coverage.toSync')}
      />
      <StatCard
        label={t('vnnic.coverage.projected')}
        icon={Globe2}
        value={formatCount(data.projected_international)}
        hint={t('vnnic.coverage.projectedHint')}
        to={`${VNNIC_BASE}/international-reports`}
        linkLabel={t('vnnic.coverage.toReports')}
      />
    </StatGrid>
  );
}

function VnRatios({ data }: { data: VnnicCoverage }) {
  const { t } = useTranslation('compliance');
  const rows: { label: string; value: number; of: number; caption: string }[] = [
    {
      label: t('vnnic.coverage.vnManaged'),
      value: data.vn_managed,
      of: data.managed_domains,
      caption: t('vnnic.coverage.vnManagedCaption'),
    },
    {
      label: t('vnnic.coverage.dossierComplete'),
      value: data.vn_dossier_complete,
      of: data.vn_managed,
      caption: t('vnnic.coverage.ofVn'),
    },
    {
      label: t('vnnic.coverage.acknowledged'),
      value: data.vn_acknowledged,
      of: data.vn_managed,
      caption: t('vnnic.coverage.ofVn'),
    },
  ];
  return (
    <Section title={t('vnnic.coverage.vnCoverage')}>
      <dl className="grid gap-5">
        {rows.map((r, i) => {
          const value = share(r.value, r.of);
          // The first row is a share, not a target, so it carries no status tone.
          const tone = i === 0 ? 'info' : ratioTone(value);
          return (
            <div key={r.label} className="grid gap-1.5">
              <dt className="flex items-baseline justify-between gap-2 text-sm">
                <span className="font-medium">{r.label}</span>
                <span className="text-xs whitespace-nowrap text-muted-foreground tabular">
                  <strong className="text-foreground">{formatCount(r.value)}</strong> / {formatCount(r.of)} · {formatPercent(value)}
                </span>
              </dt>
              <dd className="grid gap-1">
                <Meter value={value} tone={tone} label={`${r.label}, ${r.caption}`} />
                <span className="text-xs text-muted-foreground">{r.caption}</span>
              </dd>
            </div>
          );
        })}
      </dl>
    </Section>
  );
}

function ExceptionQueues({ data, selected, onSelect }: { data: VnnicCoverage; selected: VnnicGap | null; onSelect: (gap: VnnicGap | null) => void }) {
  const { t } = useTranslation('compliance');
  const label = useEnumLabel();
  return (
    <Section
      title={t('vnnic.coverage.queues')}
      description={t('vnnic.coverage.queuesHint')}
      actions={
        selected ? (
          <Button variant="ghost" size="sm" onClick={() => onSelect(null)}>
            {t('vnnic.coverage.showAll')}
          </Button>
        ) : null
      }
    >
      <ul className="grid gap-1.5 sm:grid-cols-2">
        {GAP_ORDER.map((g) => {
          const count = data.gaps[g];
          const active = selected === g;
          return (
            <li key={g}>
              <button
                type="button"
                className={cn(
                  'flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors hover:bg-muted/60',
                  active && 'border-primary bg-accent ring-1 ring-primary',
                  count === 0 && 'text-muted-foreground',
                )}
                aria-pressed={active}
                onClick={() => onSelect(active ? null : g)}
                title={label('gapHint', g)}
              >
                <span className="min-w-0">{count > 0 ? <EnumBadge group="gap" value={g} tones={GAP} /> : <span className="text-xs">{label('gap', g)}</span>}</span>
                <span className={cn('font-semibold tabular', count > 0 && 'text-foreground')}>{formatCount(count)}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

function ExceptionList({ gap }: { gap: VnnicGap | null }) {
  const { t } = useTranslation('compliance');
  const label = useEnumLabel();
  const result = useApi((signal) => api.listVnnicExceptions({ gap: gap ?? undefined, limit: EXCEPTION_LIMIT }, { signal }), [gap]);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (gap) heading.current?.focus();
  }, [gap]);

  const columns: Column<Exception>[] = [
    {
      key: 'domain',
      header: t('vnnic.coverage.col.domain'),
      cell: (e) => (
        <Link to={`/domains/${encodeURIComponent(e.ascii_name)}`} className="font-medium text-primary hover:underline">
          {e.ascii_name}
        </Link>
      ),
    },
    {
      key: 'tld',
      header: t('vnnic.coverage.col.tldClass'),
      cell: (e) => <code className="text-xs">{e.tld_class}</code>,
    },
    {
      key: 'gap',
      header: t('vnnic.coverage.col.gap'),
      cell: (e) => <EnumBadge group="gap" value={e.gap} tones={GAP} />,
    },
    {
      key: 'resolve',
      header: t('vnnic.coverage.col.resolve'),
      cell: (e) => {
        const r = gapResolution(e.gap);
        return (
          <Link to={r.to} className="inline-flex items-center gap-1 text-primary hover:underline">
            {t(`vnnic.resolveIn.${r.key}`)} <ArrowRight className="size-3.5" aria-hidden="true" />
          </Link>
        );
      },
    },
  ];

  return (
    <Section
      flush
      title={
        <h2 ref={heading} tabIndex={-1} className="outline-none">
          {gap ? t('vnnic.coverage.exceptionsFor', { gap: label('gap', gap) }) : t('vnnic.coverage.allExceptions')}
        </h2>
      }
      description={gap ? label('gapHint', gap) : undefined}
    >
      {result.status === 'loading' ? (
        <div className="px-5">
          <Loading label={t('vnnic.coverage.loadingExceptions')} rows={3} />
        </div>
      ) : null}
      {result.status === 'error' ? (
        <div className="px-5 pb-5">
          <ErrorState error={result.error} onRetry={result.reload} title={t('vnnic.coverage.exceptionsError')} />
        </div>
      ) : null}
      {result.status === 'success' ? (
        <>
          <DataTable
            columns={columns}
            rows={result.data.items}
            rowKey={(e) => `${e.domain_id}-${e.gap}`}
            empty={<EmptyState title={gap ? t('vnnic.coverage.queueEmpty') : t('vnnic.coverage.noExceptions')}>{t('vnnic.coverage.allCovered')}</EmptyState>}
          />
          {result.data.items.length >= EXCEPTION_LIMIT ? (
            <p className="border-t px-5 py-3 text-xs text-muted-foreground">{t('common.showingFirst', { count: EXCEPTION_LIMIT })}</p>
          ) : null}
        </>
      ) : null}
    </Section>
  );
}

type Mismatch = VnnicRegistryList['mismatches'][number];

const MISMATCH_TONE: Record<VnnicRegistryMismatchKind, Tone> = {
  MISSING_AT_VNNIC: 'crit',
  UNKNOWN_LOCALLY: 'serious',
};

/** VNNIC's `GET /domains` list compared with our .vn domains (vnnic-reconciliation-worker, or "Đối chiếu ngay"). */
function RegistryList() {
  const { t } = useTranslation('compliance');
  const result = useApi((signal) => api.getVnnicRegistryList({ limit: EXCEPTION_LIMIT }, { signal }), []);
  const check = useWrite((_: null, opts) => api.checkVnnicRegistryList(opts));
  const checkNow = async () => {
    if (await check.run(null)) result.reload();
  };
  const columns: Column<Mismatch>[] = [
    {
      key: 'domain',
      header: t('vnnic.registryList.col.domain'),
      cell: (m) =>
        m.domain_id ? (
          <Link to={`/domains/${encodeURIComponent(m.ascii_name)}`} className="font-medium text-primary hover:underline">
            {m.ascii_name}
          </Link>
        ) : (
          <span className="font-medium">{m.ascii_name}</span>
        ),
    },
    {
      key: 'kind',
      header: t('vnnic.registryList.col.kind'),
      cell: (m) => <Badge tone={MISMATCH_TONE[m.kind]}>{t(`vnnic.registryList.kind.${m.kind}`)}</Badge>,
    },
    {
      key: 'state',
      header: t('vnnic.registryList.col.localState'),
      cell: (m) => (m.local_state ? <code className="text-xs">{m.local_state}</code> : <span className="text-muted-foreground">{t('vnnic.registryList.notManaged')}</span>),
    },
    {
      key: 'expiry',
      header: t('vnnic.registryList.col.vnnicExpiry'),
      cell: (m) => <span className="whitespace-nowrap">{m.vnnic_expired_date ?? '—'}</span>,
    },
  ];
  return (
    <div id="registry-list" className="scroll-mt-20">
      <Section
        title={t('vnnic.registryList.title')}
        description={t('vnnic.registryList.description')}
        actions={
          <Button type="button" size="sm" variant="outline" onClick={() => void checkNow()} disabled={check.busy}>
            <RefreshCw className={cn(check.busy && 'animate-spin')} aria-hidden="true" />
            {check.busy ? t('vnnic.registryList.checking') : t('vnnic.registryList.checkNow')}
          </Button>
        }
        flush
      >
        {check.state.phase === 'error' ? (
          <div className="px-5 pb-3">
            <WriteError error={check.state.error} />
          </div>
        ) : null}
        {result.status === 'loading' ? (
          <div className="px-5">
            <Loading label={t('vnnic.registryList.loading')} rows={2} />
          </div>
        ) : null}
        {result.status === 'error' ? (
          <div className="px-5 pb-5">
            <ErrorState error={result.error} onRetry={result.reload} title={t('vnnic.registryList.loadError')} />
          </div>
        ) : null}
        {result.status === 'success' ? <RegistryListBody data={result.data} columns={columns} /> : null}
      </Section>
    </div>
  );
}

function RegistryListBody({ data, columns }: { data: VnnicRegistryList; columns: Column<Mismatch>[] }) {
  const { t } = useTranslation('compliance');
  const done = data.latest_completed;
  const failed = data.latest && data.latest.status === 'FAILED' ? data.latest : null;
  if (!done && !failed) {
    return (
      <div className="px-5 pb-5">
        <EmptyState title={t('vnnic.registryList.never')}>{t('vnnic.registryList.neverHint')}</EmptyState>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="space-y-2 px-5">
        {failed ? (
          <Callout tone="warn">
            {t('vnnic.registryList.lastFailed', {
              rel: formatRelative(failed.fetched_at),
              why: t(`vnnic.registryList.failure.${failed.error_code ?? 'OTHER'}`, { defaultValue: t('vnnic.registryList.failure.OTHER') }),
            })}
          </Callout>
        ) : null}
        {done ? (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span title={formatDateTime(done.fetched_at)}>
              {t('vnnic.registryList.checkedAt', { rel: formatRelative(done.fetched_at) })}
            </span>
            <span className="text-muted-foreground">
              {t('vnnic.registryList.counts', {
                vnnic: formatCount(done.vnnic_count ?? 0),
                local: formatCount(done.local_count ?? 0),
                grace: formatDuration(done.grace_seconds),
              })}
            </span>
            <Badge tone={(done.missing_at_vnnic ?? 0) > 0 ? 'crit' : 'ok'}>
              {t('vnnic.registryList.missingAtVnnic')}: {formatCount(done.missing_at_vnnic ?? 0)}
            </Badge>
            <Badge tone={(done.unknown_locally ?? 0) > 0 ? 'serious' : 'ok'}>
              {t('vnnic.registryList.unknownLocally')}: {formatCount(done.unknown_locally ?? 0)}
            </Badge>
          </div>
        ) : null}
      </div>
      {done ? (
        <DataTable
          columns={columns}
          rows={data.mismatches}
          rowKey={(m) => `${m.kind}-${m.ascii_name}`}
          empty={<EmptyState title={t('vnnic.registryList.noMismatches')}>{t('vnnic.registryList.noMismatchesHint')}</EmptyState>}
        />
      ) : (
        <div className="pb-5" />
      )}
    </div>
  );
}

function CountTable<K extends string>({
  title,
  counts,
  order,
  group,
  tones,
  link,
}: {
  title: string;
  counts: Partial<Record<K, number>>;
  order: readonly K[];
  group: EnumGroup;
  tones: Readonly<Record<string, { tone: Tone }>>;
  link?: (key: K) => string;
}) {
  const { t } = useTranslation('compliance');
  const known = order.filter((k) => (counts[k] ?? 0) > 0);
  const extra = (Object.keys(counts) as K[]).filter((k) => !order.includes(k) && (counts[k] ?? 0) > 0);
  const rows = [...known, ...extra];
  const render = (k: K): ReactNode => <EnumBadge group={group} value={k} tones={tones} />;
  return (
    <Section title={title} flush>
      {rows.length === 0 ? (
        <div className="px-5 pb-5">
          <EmptyState title={t('vnnic.coverage.nothingToCount')} />
        </div>
      ) : (
        <ul className="divide-y border-t">
          {rows.map((k) => (
            <li key={k} className="flex items-center justify-between gap-3 px-5 py-2.5">
              {link ? (
                <Link to={link(k)} className="hover:opacity-80">
                  {render(k)}
                </Link>
              ) : (
                render(k)
              )}
              <span className="font-semibold tabular">{formatCount(counts[k] ?? 0)}</span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function LatestReports({ data }: { data: VnnicCoverage }) {
  const { t } = useTranslation('compliance');
  const columns: Column<ReportRow>[] = [
    {
      key: 'report',
      header: t('vnnic.reportCols.report'),
      cell: (r) => (
        <Link to={`${VNNIC_BASE}/international-reports?type=${r.report_type}`} className="whitespace-nowrap text-primary hover:underline">
          {t(`vnnic.reportType.${r.report_type}`)}
        </Link>
      ),
    },
    {
      key: 'period',
      header: t('vnnic.reportCols.period'),
      cell: (r) => <span className="whitespace-nowrap">{periodLabel(r.report_type, r.period_start)}</span>,
    },
    {
      key: 'rev',
      header: t('vnnic.reportCols.revision'),
      cell: (r) => `r${r.revision}`,
    },
    {
      key: 'status',
      header: t('vnnic.reportCols.status'),
      cell: (r) => <EnumBadge group="reportStatus" value={r.status} tones={REPORT_STATUS} />,
    },
    {
      key: 'records',
      header: t('vnnic.reportCols.records'),
      numeric: true,
      cell: (r) => formatCount(r.record_count),
    },
    {
      key: 'invalid',
      header: t('vnnic.reportCols.invalid'),
      numeric: true,
      cell: (r) => (r.invalid_count > 0 ? <Badge tone="crit">{formatCount(r.invalid_count)}</Badge> : '0'),
    },
    {
      key: 'schema',
      header: t('vnnic.reportCols.schema'),
      cell: (r) => <code className="text-xs">{r.schema_version}</code>,
    },
    {
      key: 'generated',
      header: t('vnnic.reportCols.generated'),
      cell: (r) => <span className="whitespace-nowrap">{formatDateTime(r.generated_at)}</span>,
    },
  ];
  return (
    <Section
      title={t('vnnic.coverage.latestReports')}
      description={t('vnnic.coverage.schemaNote')}
      actions={
        <Button asChild variant="ghost" size="sm">
          <Link to={`${VNNIC_BASE}/international-reports`}>
            {t('vnnic.coverage.allBatches')} <ArrowRight />
          </Link>
        </Button>
      }
      flush
    >
      <DataTable columns={columns} rows={data.reports} rowKey={(r) => `${r.report_type}-${r.period_start}`} empty={<EmptyState title={t('vnnic.coverage.noReports')} />} />
    </Section>
  );
}

export function VnnicCoveragePage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  useDocumentTitle(t('vnnic.coverage.title'));
  const [params, setParams] = useSearchParams();
  const rawGap = params.get('gap');
  const gap = isGap(rawGap) ? rawGap : null;
  const result = useApi((signal) => api.getVnnicCoverage({ signal }), []);

  const selectGap = (next: VnnicGap | null) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next) p.set('gap', next);
        else p.delete('gap');
        return p;
      },
      { replace: true },
    );

  return (
    <>
      <PageHeader
        title={t('vnnic.coverage.title')}
        crumbs={[{ label: t('crumbs.compliance') }, { label: t('crumbs.vnnic') }]}
        subtitle={t('vnnic.coverage.subtitle')}
        actions={
          <Button size="sm" variant="outline" onClick={result.reload} disabled={result.status === 'loading'}>
            <RefreshCw /> {tc('action.refresh')}
          </Button>
        }
      />

      {result.status === 'loading' ? <Loading label={t('vnnic.coverage.loading')} rows={4} /> : null}
      {result.status === 'error' ? <ErrorState error={result.error} onRetry={result.reload} title={t('vnnic.coverage.loadError')} /> : null}

      {result.status === 'success' ? (
        <div className="space-y-6">
          <Summary data={result.data} />
          <div className="grid gap-6 lg:grid-cols-2">
            <ExceptionQueues data={result.data} selected={gap} onSelect={selectGap} />
            <VnRatios data={result.data} />
          </div>
          <ExceptionList gap={gap} />
          <RegistryList />
          <div className="grid gap-6 lg:grid-cols-2">
            <CountTable<DossierStatus>
              title={t('vnnic.coverage.dossiersByStatus')}
              counts={result.data.dossiers}
              order={Object.keys(DOSSIER) as DossierStatus[]}
              group="dossierStatus"
              tones={DOSSIER}
            />
            <CountTable
              title={t('vnnic.coverage.syncByStatus')}
              counts={result.data.sync}
              order={SYNC_STATUS_ORDER}
              group="syncStatus"
              tones={SYNC_STATUS}
              link={(k) => `${VNNIC_BASE}/dossier-sync?status=${encodeURIComponent(k)}`}
            />
          </div>
          <LatestReports data={result.data} />
        </div>
      ) : null}
    </>
  );
}
